<template>
    <div class="live-page mega-lab-page">
        <div class="case-study">

<h1>MEGA Lab Hawaii</h1>

<p class="meta"><strong>Hawaiʻi Island, USA</strong></p>
<p><strong>System:</strong> Octopus™ Pan-Tilt-Zoom Underwater Observatory</p>
<p><strong>Application:</strong> Research, Education, Conservation & Public Engagement</p>

            <div class="pink-line"></div>

            <div class="deployment-scene" aria-hidden="true">
                <MotionMedia :src="previewVideo" />
            </div>

<section class="deployment-live">
    <h2>Watch Live</h2>

    <div class="video-container">
        <iframe
            :key="$route.fullPath"
            loading="lazy"
            width="960"
            height="540"
            src="https://www.youtube.com/embed/jOp_aAXsYmE"
            title="MEGA Lab Hawaii"
            frameborder="0"
            allowfullscreen>
        </iframe>
    </div>
</section>

<section>
    <h2>Project Overview</h2>

    <p>
        The MEGA Lab Camera is a live underwater observatory located off the Kona Coast
        of Hawaiʻi Island. Developed through a collaboration between The MEGA Lab,
        Aqualink, and Fathom Ocean, the project provides a continuous real-time view
        of a Hawaiian coral reef ecosystem while supporting scientific research,
        environmental monitoring, and public education.
    </p>

    <p>
        The observatory was designed to create a stronger connection between people
        and coral reefs by making marine ecosystems accessible to researchers,
        students, and the public 24 hours a day.
    </p>
</section>

<div class="pink-line"></div>

<section>
    <h2>The Challenge</h2>

    <p>
        Coral reefs around the world face increasing pressure from climate change,
        thermal stress, and other environmental impacts. Researchers needed a way
        to continuously monitor reef conditions, observe marine life, and engage
        the public in ocean conservation.
    </p>

    <p>
        Traditional field surveys provide only snapshots in time. The project team
        sought a long-term monitoring solution capable of collecting continuous visual
        observations while supporting scientific research and educational outreach.
    </p>
</section>

<div class="pink-line"></div>

<section>
    <h2>The Fathom Ocean Solution</h2>

    <p>
        Fathom Ocean deployed an Octopus™ Pan-Tilt-Zoom underwater observatory system
        capable of long-term operation in a demanding marine environment.
    </p>

    <p><strong>Key system capabilities include:</strong></p>

    <ul>
        <li>Remote pan-tilt-zoom control</li>
        <li>Self-cleaning optics</li>
        <li>Continuous live streaming</li>
        <li>Long-term underwater deployment</li>
        <li>Remote monitoring and management</li>
        <li>Integration with environmental monitoring initiatives</li>
    </ul>

    <p>
        The system was installed at the Natural Energy Laboratory of Hawaii Authority (NELHA),
        where existing ocean infrastructure supports long-term scientific observation.
    </p>
</section>

<div class="pink-line"></div>

<section>
    <h2>Impact & Results</h2>

    <p>
        The MEGA Lab Camera has become an important platform for marine science,
        conservation, and education.
    </p>

    <p><strong>Project outcomes include:</strong></p>

    <ul>
        <li>Continuous observation of Hawaiian coral reef ecosystems</li>
        <li>Support for coral reef monitoring and environmental research</li>
        <li>Educational programming for classrooms and students worldwide</li>
        <li>Public engagement through 24/7 livestream access</li>
        <li>Development of AI and machine-learning tools for marine monitoring</li>
        <li>Collection of valuable visual data supporting reef conservation efforts</li>
    </ul>

    <p>
        The project has documented a wide variety of marine life including reef fish,
        sharks, sea turtles, octopus, eels, and other species that contribute to
        Hawaiʻi's unique marine ecosystems.
    </p>
</section>

<div class="pink-line"></div>

<section>
    <h2>Why This Project Matters</h2>

    <p>
        The MEGA Lab Camera demonstrates how long-term underwater observatories can
        transform the way we study, understand, and protect marine environments.
    </p>

    <p>
        By combining real-time video monitoring, environmental data collection,
        and public participation, the project creates a powerful platform for both
        scientific discovery and ocean stewardship.
    </p>

    <p>
        The observatory also serves as a living classroom, allowing students and
        educators around the world to experience Hawaiian coral reefs without ever
        entering the water.
    </p>
</section>

<div class="pink-line"></div>

<section>
    <h2>System Configuration</h2>

    <ul>
        <li>Octopus™ PTZ Underwater Observatory</li>
        <li>Self-cleaning technology</li>
        <li>Remote operation</li>
        <li>Continuous livestreaming</li>
        <li>Long-term deployment</li>
        <li>Research and education focused installation</li>
    </ul>
</section>

<div class="pink-line"></div>

<section>
    <h2>Interested in a Similar Project?</h2>

    <p>
        Whether you're a research institution, aquarium, conservation organization,
        utility, or educational facility, Fathom Ocean can help design an underwater
        monitoring solution tailored to your goals.
    </p>

    <b-button @click="$router.push('/contact')">
        Schedule a Consultation
    </b-button>
</section>
        </div>
    </div>
</template>

<script>
import MotionMedia from '@/components/journey/MotionMedia.vue'
import previewVideo from '@/assets/videos/hawaii_small.mp4'

export default {
    name: 'LiveCameras',
    components: {
        MotionMedia,
    },
    data() {
        return {
            previewVideo,
        }
    },
}
</script>
